import React from 'react';
import {Divider} from '@albert-io/atomic';

import {criteriaFromRubric, rubricWithCriteria, toPlainRubric} from 'lib/rubric/criteria';

import {isTotalAdjusted, sumDefinedCriteria} from '../shared/FrqEvaluationSummary/evaluationTotals';
import GradedEvaluationBody from '../shared/FrqEvaluationSummary/GradedEvaluationBody.react';
import ReleasedGradeBanner from '../shared/FrqEvaluationSummary/ReleasedGradeBanner.react';
import type {ManualEvaluation} from '../shared/FrqEvaluationSummary/FrqEvaluationSummary.types';

type Props = {
  /** The question's rubric as authored — an Immutable Map in app code, plain in tests. */
  rubric: unknown;
  /** The student's released evaluation, straight off their own guess. */
  evaluation: ManualEvaluation;
};

/**
 * A student's released FRQ grade: the correctness banner, then the per-criterion breakdown with
 * their teacher's feedback.
 *
 * Renders through the same `GradedEvaluationBody` as the teacher's preview tab, so "preview what
 * the student will see" stays true by construction rather than by two implementations agreeing.
 */
export default function ReleasedGradeView({rubric, evaluation}: Props) {
  const criteria = criteriaFromRubric(toPlainRubric(rubric));
  const max = criteria.reduce((total, criterion) => total + criterion.maxScore, 0);
  // A directly-set overall score is the teacher's override and wins; otherwise the criteria sum is
  // the grade, which is how the server derived it at release.
  const earned = evaluation.overallScore ?? sumDefinedCriteria(evaluation, criteria).earned;

  return (
    <div data-testid='student-released-grade'>
      <GradedEvaluationBody
        rubric={criteria.length > 0 ? rubricWithCriteria(criteria) : null}
        evaluation={evaluation}
        banner={
          <ReleasedGradeBanner
            earned={earned}
            max={max}
            isAdjusted={isTotalAdjusted(evaluation, criteria)}
          />
        }
      />
      {/* Closes the evaluation off from the student's response below it.
          `mt-x0` is load-bearing — the global `hr` rule carries a 2em top margin that `spacing`
          only cancels on the bottom. `mx-x1` matches the inset `GradedEvaluationBody` applies to
          the criterion dividers, which this one sits outside of. */}
      <Divider spacing={3} className='mt-x0 mx-x1' />
    </div>
  );
}
